// ---------------------------------------------------------------------------
// MetricTile (with its Trend and Delta), Sparkline and ProgressMeter, ported
// from the design system (R9; the tile's trend, R6g, in R10f)
//
// <sdr-metric-tile>: one big figure with its change and a small chart of its
// trend, the largest object on an overview. A light-DOM Lit element, because
// the system's tile holds state and measures (R6g):
//   - the trend (2 or more points) is drawn in real pixels: the plot is
//     measured as the system's useBoxSize does (getBoundingClientRect, width
//     and height rounded, once after the render that draws the plot and on
//     every ResizeObserver report; without a ResizeObserver only once), and
//     the svg gets that width, height and viewBox. Until it has measured (on
//     the server, in a page without layout) no marks are drawn, only the
//     trend's frame, tab stop and summary, as the system writes it there.
//   - one series is a line over a wash, its latest point a dot; with
//     trendPrevious the previous period is a dashed line behind it, a legend
//     names both and the root gets sdr-trend--compare (the current line moves
//     up to ink, in the stylesheet). It may run longer than trend (a period
//     in progress).
//   - the readout: a crosshair and a readout on the nearest point while the
//     pointer moves over the plot, gone when it leaves; ArrowLeft and
//     ArrowRight one point (from nothing: the latest of the current series),
//     Home and End the ends, Escape hides it (and only then is it handled).
//     The keys are read as React reads them (reactKey). State {at, byKey},
//     as the system's: a key sets byKey, the pointer clears it.
//   - a tile that does not filter gives its plot the tab stop (G4): role
//     group, "<label> trend", described by the summary; focus shows the
//     latest point, blur hides it, and a polite live region says each point
//     reached by key. A filter tile keeps its single tab stop and is
//     described by the summary itself; the same keys on the tile move the
//     readout first, then Enter and Space press it as before, and its blur
//     hides the readout too. Nothing focusable is inside the button.
//   - the summary (start, end, low and high of each series, both named with
//     a comparison) is a hidden span; trendFormat writes the values, by
//     default the number in the locale with the unit ('%' as a percentage).
//   - variant hero: the figure's side wrapped in sdr-metric__info, a taller
//     plot with clean ticks (whole numbers for a count), the end values beside
//     the line ends (the previous one only when 14px or more apart) and an
//     axis with the first, last and, if it fits, middle label; the readout
//     sits beside the crosshair.
// The system keeps that state in the tile (useState) and the plot's size in
// its Trend, which mounts with a trend and goes with it: so does what the port
// measured, and a new trend gets a new gradient id, as a new useId. When the
// element leaves the page it lets go of its observer and its readout (no
// pointerleave or blur comes after a removal); back in the page it measures
// again. What a redraw itself causes is not heard, as in React, whose events
// are off while it commits: Chrome blurs the focused plot as the trend goes,
// and the port, hearing it, cleared the point the system keeps for the trend
// that comes back (found in Chrome, R10f). The state does not change while Lit
// commits (`committing`, as in heatmap.ts and donut-chart.ts). A pointer's
// update (a move, the leave) is drawn in the next task (chart-core.ts's
// afterTask), as React renders its continuous lane, and what comes behind it
// in the same task waits too: Chrome sends a click's focus right after the
// move it held back, so the system's focus finds no point yet and shows the
// latest, and so does the port (found in Chrome, R10f).
//
// metricTile() is the same tile as a template, for a holder that has no state
// to give: a figure without a trend is the whole port (the system's tile has
// nothing to hold then), a Tooltip's trigger, a server render. A trend in it
// is what the system writes before it has measured: no marks and no readout;
// without onClick a tab stop whose keys move nothing; new ids on every call.
// Use <sdr-metric-tile> for a trend: the template says so once per tile, in
// the console (R10f).
//
// As a filter (onClick; R6, S6: only then, pressed alone is a figure) the tile
// is a toggle button (role button, in the tab order, aria-pressed) that
// presses once per key, as a native button does (S5): Enter on its keydown,
// Space on its keyup after a keydown on the tile; a held key does not repeat
// and a blur between keydown and keyup disarms Space. pressed is the caller's,
// the tile holds no state of its own but that armed Space, kept per tile
// element (the system's useRef).
//
// sparkline(): the trend the tile drew before R6g, a single stroke scaled to
// the points' own range; nothing for fewer than two points. The system keeps
// its Sparkline in the bundle, unpublished and no longer drawn by the tile,
// for fragments that call it; so does the port.
// delta(): the change, an arrow for up or down (none for flat) and the
// comparison window in muted words.
// progressMeter(): kind 'limit' (the default) is how much of a limit is used,
// a meter. It turns caution at cautionAt (0.8 of max unless given) and
// critical at criticalAt (1 unless given), or takes an explicit tone; the
// footnote says so in words with an icon, a hint replaces those words, and
// hint null hides the footnote. kind 'progress' (S17) is how far a task has
// come, a progressbar: no thresholds, no tick and never the cap words unless
// cautionAt or criticalAt are given, a tone only when given. Either way the
// value read out stays within max (aria-valuenow), and past it
// aria-valuetext says the real one ("212 of 200").
//
// Sparkline, Delta, ProgressMeter and metricTile() are template functions
// with the system's prop names (className). The element renames one prop,
// because it would shadow its own HTML attribute: className → tileClass.
// Sparkline and Delta are not published on window.SDR; they are exported
// here with the system's props ({ data }, { delta }).
//
// Two helpers from helpers.ts stand in for what React does by itself: node()
// draws a React node prop (hint, badge) as React would, true and false as
// nothing where Lit would write "true"; reactKey() reads a key as React's
// onKeyDown reads it (getEventKey), so the legacy "Spacebar", "Left" and
// "Esc" and a keydown that only has a keyCode (13, 32, 37) act as in the
// system. The trend's arithmetic (its domain, ticks and gutters) is the
// tile's own in the system, not the charts' (chart-core.ts), so it is written
// here, step for step.
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues, svg, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';

import { afterTask } from './chart-core.js';
import { cx, format, noBox, node, num, present, reactKey, reactStyle } from './helpers.js';
import { icon } from './icons.js';

/** The filter tiles whose Space went down on them and has not come up yet (the system's useRef per tile). */
const spaceDown = new WeakSet<EventTarget>();

export interface MetricDelta {
  /** Already formatted ("8.2%", "0.9 pts", "no change"). */
  value: string;
  direction?: 'up' | 'down' | 'flat';
  /** By meaning, not direction: a rising bounce rate is up and negative. */
  sentiment?: 'positive' | 'negative' | 'neutral';
  /** The comparison window ("vs last 7 days"). */
  label?: string;
}

export interface SparklineProps {
  data?: number[];
}

/** The trend as one stroke, no fill, scaled to the points' range; nothing for fewer than two points. */
export function sparkline(p: SparklineProps): TemplateResult | typeof nothing {
  const d = p.data || [];
  if (d.length < 2) return nothing;
  const min = Math.min(...d);
  const max = Math.max(...d);
  const span = max - min || 1;
  const line = d
    .map((v, i) => {
      const x = (i / (d.length - 1)) * 100;
      const y = 30 - ((v - min) / span) * 28;
      return `${i ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join('');
  return html`<svg class="sdr-spark" viewBox="0 0 100 32" preserveAspectRatio="none" aria-hidden="true"><path
      class="sdr-spark__line"
      d=${line}
    ></path></svg>`;
}

export interface DeltaProps {
  delta: MetricDelta;
}

/** The change beside a figure: an arrow unless flat, the value, and the comparison window. */
export function delta(p: DeltaProps): TemplateResult {
  const d = p.delta;
  const dir = d.direction || 'flat';
  const sentiment = d.sentiment || 'neutral';
  return html`<span class=${cx('sdr-delta', `sdr-delta--${sentiment}`)}
    >${dir === 'flat' ? null : icon({ name: dir === 'up' ? 'arrow-up' : 'arrow-down', size: 12 })}${d.value}${
      d.label ? html`<span class="sdr-delta__label">${` ${d.label}`}</span>` : null
    }</span
  >`;
}

// ---- The trend (the system's Trend and its helpers) ----

/** px per character of a 12px tabular figure, for the hero chart's gutters (the system's TEXT_W). */
const TEXT_W = 7;

const TREND_NAMES = ['This period', 'Previous period'] as const;

/** A clean step: 1, 2, 2.5, 5 or 10 times a power of ten (the system's niceStep). */
function niceStep(span: number, count: number): number {
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log(raw) / Math.LN10);
  const f = raw / mag;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * mag;
}

interface TrendDomain {
  lo: number;
  hi: number;
  ticks: number[];
}

/** The tile's trend shows shape: its own range with a margin. The hero prints its scale: clean ticks (trendDomain). */
function trendDomain(values: number[], hero: boolean): TrendDomain {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || Math.abs(hi) || 1;
  if (!hero) return { lo: lo - span * 0.12, hi: hi + span * 0.12, ticks: [] };
  let step = niceStep(span, 3);
  // Counts never get a tick between two whole numbers (no "0.5 meetings").
  if (step < 1 && values.every(v => v === Math.round(v))) step = 1;
  const a = Math.floor(lo / step) * step;
  let b = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  if (b - a < step) b = a + step;
  for (let t = a; t <= b + step / 2; t += step) ticks.push(Math.round(t / step) * step);
  return { lo: a, hi: b, ticks };
}

/** What a point is: its Date ("Wed, Oct 28" in the locale), its string, or "Point 3 of 7" (trendWhen). */
function trendWhen(labels: unknown, i: number, n: number): string {
  const l = labels && (labels as ArrayLike<unknown>)[i];
  if (l instanceof Date) return format.date(l, { weekday: 'short', day: 'numeric', month: 'short' });
  return l != null && l !== '' ? String(l) : `Point ${num(i + 1)} of ${num(n)}`;
}

/** How a value is written: trendFormat, a percentage for the unit '%', else the number with the unit. */
function trendFormatter(p: Pick<MetricTileProps, 'trendFormat' | 'unit'>): (v: number) => string {
  const own = p.trendFormat;
  return v =>
    own ? String(own(v)) : p.unit === '%' ? format.percent(v / 100) : `${num(v)}${p.unit ? ` ${p.unit}` : ''}`;
}

/** The trend's text alternative: where it starts and ends, its low and high, and the previous period's. */
function trendSummary(
  cur: number[],
  prev: number[] | null,
  labels: unknown,
  names: readonly unknown[],
  fmt: (v: number) => string,
): string {
  const run = (d: number[]) =>
    `${fmt(d[0] as number)} to ${fmt(d[d.length - 1] as number)}, low ${fmt(Math.min(...d))}, high ${fmt(Math.max(...d))}`;
  const n = cur.length;
  const when =
    labels && (labels as ArrayLike<unknown>).length >= n
      ? `${trendWhen(labels, 0, n)} to ${trendWhen(labels, n - 1, n)}`
      : `${num(n)} points`;
  // With a comparison both series are named, so neither is left to its position in the sentence.
  if (prev) return `Trend, ${when}. ${names[0]}: ${run(cur)}. ${names[1]}: ${run(prev)}.`;
  return `Trend, ${when}: ${run(cur)}.`;
}

type SetActive = (at: number | null, byKey: boolean) => void;

/** Arrows move the readout one point, Home and End to the ends, Escape hides it. True when the key was used. */
function trendKey(e: KeyboardEvent, at: number | null, n: number, m: number, set: SetActive): boolean {
  const k = reactKey(e);
  let next: number | null;
  if (k === 'ArrowRight' || k === 'ArrowLeft')
    next = at == null ? n - 1 : Math.max(0, Math.min(m - 1, at + (k === 'ArrowRight' ? 1 : -1)));
  else if (k === 'Home') next = 0;
  else if (k === 'End') next = m - 1;
  else if (k === 'Escape' && at != null) next = null;
  else return false;
  e.preventDefault();
  set(next, true);
  return true;
}

/** A series' short stroke in the legend and the readout (the system's TrendKey). */
const trendKeyMark = (previous: boolean) =>
  html`<span class=${cx('sdr-trend__key', previous && 'sdr-trend__key--prev')} aria-hidden="true"></span>`;

interface TrendDraw {
  data: number[];
  previous: number[] | null;
  labels: unknown;
  names: readonly unknown[];
  format: (v: number) => string;
  hero: boolean;
  label: unknown;
  focusable: boolean;
  active: number | null;
  byKey: boolean;
  onActive: SetActive;
  /** The pointer's setActive (a move, the leave): React draws it in its next task (the continuous lane). */
  onPointer: SetActive;
  summaryId: string;
  summary: string;
  /** The plot's measured [width, height] in whole px; null until it has been measured. */
  size: [number, number] | null;
  gid: string;
  plot?: ((el?: Element) => void) | undefined;
}

/** The system's Trend: the plot, its marks once measured, the readout, the axis, the legend and the summary. */
function trend(p: TrendDraw): TemplateResult {
  const cur = p.data;
  const prev = p.previous;
  const hero = p.hero;
  const fmt = p.format;
  const names = p.names;
  const n = cur.length;
  const m = prev ? Math.max(n, prev.length) : n;
  const at = p.active;
  const dom = trendDomain(prev ? cur.concat(prev) : cur, hero);
  const sameEnd = !prev || prev.length === n;
  const endText = hero && sameEnd ? [fmt(cur[n - 1] as number), prev ? fmt(prev[n - 1] as number) : ''] : ['', ''];
  const padL = hero ? Math.max(...dom.ticks.map(t => fmt(t).length)) * TEXT_W + 10 : 1;
  const padR =
    hero && sameEnd ? Math.max((endText[0] as string).length, (endText[1] as string).length) * TEXT_W + 14 : 6;
  const W = p.size ? p.size[0] : 0;
  const H = p.size ? p.size[1] : 0;
  const padT = 6;
  const padB = 6;
  const xOf = (i: number) => padL + (m > 1 ? i / (m - 1) : 0.5) * Math.max(0, W - padL - padR);
  const yOf = (v: number) => padT + (1 - (v - dom.lo) / (dom.hi - dom.lo)) * Math.max(0, H - padT - padB);
  const line = (d: number[]) => d.map((v, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)} ${yOf(v).toFixed(1)}`).join('');
  /** The nearest point to the pointer, read from the plot as it is now (the system's pick). */
  const pick = (e: PointerEvent) => {
    const el = e.currentTarget as Element | null;
    if (!el || m < 2) return;
    const b = el.getBoundingClientRect();
    const t = (e.clientX - b.left - padL) / Math.max(1, b.width - padL - padR);
    p.onPointer(Math.max(0, Math.min(m - 1, Math.round(t * (m - 1)))), false);
  };
  let marks: TemplateResult | typeof nothing = nothing;
  let tip: TemplateResult | typeof nothing = nothing;
  if (W > 0 && H > 0) {
    const base = (H - padB).toFixed(1);
    const yEnd = yOf(cur[n - 1] as number);
    const yPrevEnd = prev && sameEnd ? yOf(prev[n - 1] as number) : null;
    const ticks = hero
      ? dom.ticks.map(t => {
          const y = Math.round(yOf(t)) + 0.5;
          return svg`<line class="sdr-trend__grid" x1=${padL} x2=${W - padR} y1=${y} y2=${y}></line><text class="sdr-trend__tick" x=${padL - 10} y=${y + 4} text-anchor="end">${fmt(t)}</text>`;
        })
      : nothing;
    const both = yPrevEnd != null && Math.abs(yPrevEnd - yEnd) >= 14;
    const ends = endText[0]
      ? svg`<text class="sdr-trend__end sdr-trend__end--current" x=${xOf(n - 1) + 10} y=${yEnd + 4}>${endText[0]}</text>${
          both
            ? svg`<text class="sdr-trend__end" x=${xOf(n - 1) + 10} y=${(yPrevEnd as number) + 4}>${endText[1]}</text>`
            : nothing
        }`
      : nothing;
    const wash = `${line(cur)}L${xOf(n - 1).toFixed(1)} ${base}L${xOf(0).toFixed(1)} ${base}Z`;
    const cross = Math.round(xOf(at ?? 0)) + 0.5;
    marks = html`<svg class="sdr-trend__svg" width=${W} height=${H} viewBox=${`0 0 ${W} ${H}`} aria-hidden="true" focusable="false"><defs><linearGradient id=${p.gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" class="sdr-trend__wash-stop" stop-opacity="0.16"></stop><stop offset="1" class="sdr-trend__wash-stop" stop-opacity="0"></stop></linearGradient></defs>${ticks}<path class="sdr-trend__wash" fill=${`url(#${p.gid})`} d=${wash}></path>${
      at != null ? svg`<line class="sdr-trend__cross" x1=${cross} x2=${cross} y1="0" y2=${H}></line>` : nothing
    }${prev ? svg`<path class="sdr-trend__prev" d=${line(prev)}></path>` : nothing}<path class="sdr-trend__line" d=${line(cur)}></path>${ends}${
      at != null && prev && at < prev.length
        ? svg`<circle class="sdr-trend__dot sdr-trend__dot--prev" cx=${xOf(at)} cy=${yOf(prev[at] as number)} r="3"></circle>`
        : nothing
    }<circle class="sdr-trend__dot" cx=${xOf(n - 1)} cy=${yEnd} r="4"></circle>${
      at != null && at < n && at !== n - 1
        ? svg`<circle class="sdr-trend__dot" cx=${xOf(at)} cy=${yOf(cur[at] as number)} r="4"></circle>`
        : nothing
    }</svg>`;
    if (at != null) {
      const x = xOf(at);
      const share = W ? (x / W) * 100 : 0;
      const right = share > 55;
      const style = hero
        ? right
          ? { right: W - x + 12 }
          : { left: x + 12 }
        : { left: x, transform: `translateX(-${share.toFixed(1)}%)` };
      tip = html`<div class=${cx('sdr-trend__tip', hero && 'sdr-trend__tip--side')} aria-hidden="true" style=${reactStyle(style)}><div class="sdr-trend__tip-when">${trendWhen(p.labels, at, m)}</div>${
        at < n
          ? html`<div class="sdr-trend__tip-row">${prev ? trendKeyMark(false) : nothing}<span class="sdr-trend__tip-value">${fmt(cur[at] as number)}</span>${
              prev ? html`<span class="sdr-trend__tip-name">${names[0]}</span>` : nothing
            }</div>`
          : nothing
      }${
        prev && at < prev.length
          ? html`<div class="sdr-trend__tip-row">${trendKeyMark(true)}<span class="sdr-trend__tip-value">${fmt(prev[at] as number)}</span><span class="sdr-trend__tip-name">${names[1]}</span></div>`
          : nothing
      }</div>`;
    }
  }
  const said =
    p.focusable && p.byKey && at != null
      ? `${trendWhen(p.labels, at, m)}: ${prev ? `${names[0]} ` : ''}${at < n ? fmt(cur[at] as number) : 'no value'}${
          prev && at < prev.length ? `; ${names[1]} ${fmt(prev[at] as number)}` : ''
        }`
      : '';
  // The hero's x-axis: first and last label, and the middle one when it has room (labels never collide).
  const labels = p.labels as ArrayLike<unknown> | null | undefined;
  let axis =
    hero && labels && labels.length >= 2 && W > 0
      ? [0, m > 4 ? Math.round((m - 1) / 2) : -1, m - 1].filter(i => i >= 0 && i < labels.length)
      : [];
  if (axis.length === 3) {
    const wide = (i: number) => trendWhen(p.labels, i, m).length * TEXT_W;
    const [first, middle, last] = axis as [number, number, number];
    const mid = xOf(middle);
    if (mid - wide(middle) / 2 < xOf(0) + wide(0) + 16 || mid + wide(middle) / 2 > xOf(last) - wide(last) - 16)
      axis = [first, last];
  }
  const focusable = p.focusable;
  return html`<div class=${cx('sdr-trend', hero && 'sdr-trend--hero', prev && 'sdr-trend--compare')}><div
    ${p.plot ? ref(p.plot) : nothing}
    class="sdr-trend__plot"
    tabindex=${ifDefined(focusable ? 0 : undefined)}
    role=${ifDefined(focusable ? 'group' : undefined)}
    aria-label=${ifDefined(focusable ? `${p.label} trend` : undefined)}
    aria-describedby=${ifDefined(focusable ? p.summaryId : undefined)}
    @pointermove=${pick}
    @pointerleave=${() => p.onPointer(null, false)}
    @focusin=${
      focusable
        ? () => {
            if (at == null) p.onActive(n - 1, true);
          }
        : nothing
    }
    @focusout=${focusable ? () => p.onActive(null, false) : nothing}
    @keydown=${focusable ? (e: KeyboardEvent) => void trendKey(e, at, n, m, p.onActive) : nothing}
  >${marks}${tip}${focusable ? html`<span class="sdr-visually-hidden" aria-live="polite">${said}</span>` : nothing}</div>${
    axis.length
      ? html`<div class="sdr-trend__axis" aria-hidden="true">${axis.map(
          (i, k) =>
            html`<span style=${reactStyle({
              left: xOf(i),
              transform: k === 0 ? undefined : k === axis.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
            })}>${trendWhen(p.labels, i, m)}</span>`,
        )}</div>`
      : nothing
  }${
    prev
      ? html`<div class="sdr-trend__legend" aria-hidden="true"><span class="sdr-trend__legend-item">${trendKeyMark(false)}${
          names[0]
        }</span><span class="sdr-trend__legend-item">${trendKeyMark(true)}${names[1]}</span></div>`
      : nothing
  }<span id=${p.summaryId} hidden>${p.summary}</span></div>`;
}

// ---- MetricTile ----

export interface MetricTileProps {
  label: string;
  /** A number gets the locale's separators; pass a string for decimals. */
  value: number | string;
  unit?: string;
  delta?: MetricDelta;
  /** One value per point, oldest first; 2 or more draw the chart (7 to 30 read best). */
  trend?: number[];
  /**
   * The previous period, point for point (day 1 beside day 1), dashed behind the trend; the current line then moves
   * up to ink (emphasis). May run longer than trend (a period still in progress).
   */
  trendPrevious?: number[];
  /** What each point is, for the readout, the summary and the hero's axis: Dates ("Wed, Oct 28") or strings. */
  trendLabels?: Array<Date | string>;
  /** The two series' names in the legend and the readout. Default ['This period', 'Previous period']. */
  trendNames?: [string, string];
  /** How a point's value is written. Default: the number in the locale, with the unit ('%' gives "6.4%"). */
  trendFormat?: (value: number) => string;
  /** 'hero': the one figure a page leads with, beside a larger chart with its scale. Give it two grid columns. */
  variant?: 'default' | 'hero';
  /** What the figure means, or why it is not measured ("Not measured on LinkedIn"). */
  hint?: unknown;
  /** The guardrail that applies (badge({ tone: 'caution', dot: true }, 'Cap 2%')). */
  badge?: unknown;
  /** As a filter: pressed while its filter applies. Without onClick it shows nothing. */
  pressed?: boolean;
  /** Makes it a filter: called with the click, the Enter keydown or the Space keyup. */
  onClick?: (event: MouseEvent | KeyboardEvent) => void;
  className?: string;
}

/** What a holder of the tile's state gives it to draw (the system's useState, useId and its Trend's measure). */
interface TileState {
  at: number | null;
  byKey: boolean;
  set: SetActive;
  /** The pointer's set: drawn in the next task, as React renders a pointer's update. */
  point: SetActive;
  summaryId: string;
  gid: string;
  size: [number, number] | null;
  plot?: (el?: Element) => void;
}

/** The system's MetricTile, drawn with the state it is given. */
function drawTile(p: MetricTileProps, s: TileState): TemplateResult {
  const onClick = p.onClick;
  const filter = onClick != null;
  const cur = p.trend && p.trend.length > 1 ? p.trend : null;
  const prev = cur && p.trendPrevious && p.trendPrevious.length > 1 ? p.trendPrevious : null;
  const n = cur ? cur.length : 0;
  const m = prev ? Math.max(n, prev.length) : n;
  const hero = p.variant === 'hero';
  const fmt = trendFormatter(p);
  const names = [p.trendNames?.[0] || TREND_NAMES[0], p.trendNames?.[1] || TREND_NAMES[1]] as const;
  // The keys as React's onKeyDown and onKeyUp read them (reactKey): "Spacebar" is a space, a keyCode-only 13 or 32
  // is Enter or Space. The trend takes its keys first; then Enter presses on its keydown, Space on its keyup, once
  // per press (S5).
  const down = (e: KeyboardEvent) => {
    if (cur && trendKey(e, s.at, n, m, s.set)) return;
    const key = reactKey(e);
    if (key !== 'Enter' && key !== ' ') return;
    e.preventDefault();
    if (e.repeat) return;
    if (key === ' ') spaceDown.add(e.currentTarget as EventTarget);
    else onClick?.(e);
  };
  const up = (e: KeyboardEvent) => {
    if (reactKey(e) !== ' ' || !spaceDown.delete(e.currentTarget as EventTarget)) return;
    e.preventDefault();
    onClick?.(e);
  };
  // React's onBlur listens to focusout: a blur in or of the tile disarms Space and hides the readout.
  const blur = (e: FocusEvent) => {
    spaceDown.delete(e.currentTarget as EventTarget);
    s.set(null, false);
  };
  const info = html`<div class="sdr-metric__label"><span>${p.label}</span>${node(p.badge || null)}</div><div
      class="sdr-metric__row"
    ><span class="sdr-metric__value">${num(p.value)}</span>${
      p.unit ? html`<span class="sdr-metric__unit">${p.unit}</span>` : nothing
    }</div>${p.delta ? delta({ delta: p.delta }) : nothing}`;
  const hint = present(p.hint) ? html`<div class="sdr-metric__hint">${node(p.hint)}</div>` : nothing;
  const chart = cur
    ? trend({
        data: cur,
        previous: prev,
        labels: p.trendLabels,
        names,
        format: fmt,
        hero,
        label: p.label,
        focusable: !filter,
        active: s.at,
        byKey: s.byKey,
        onActive: s.set,
        onPointer: s.point,
        summaryId: s.summaryId,
        summary: trendSummary(cur, prev, p.trendLabels, names, fmt),
        size: s.size,
        gid: s.gid,
        plot: s.plot,
      })
    : nothing;
  // One root for both variants, and the trend in the same place, so a tile that turns hero keeps its node and its
  // plot (React keeps both: the root div, and the Trend by its key).
  return html`<div
    class=${cx('sdr-metric', hero && 'sdr-metric--hero', filter && 'sdr-metric--filter', filter && p.pressed && 'is-pressed', p.className)}
    role=${ifDefined(filter ? 'button' : undefined)}
    tabindex=${ifDefined(filter ? 0 : undefined)}
    aria-pressed=${ifDefined(filter ? String(!!p.pressed) : undefined)}
    aria-describedby=${ifDefined(filter && cur ? s.summaryId : undefined)}
    @click=${filter ? onClick : undefined}
    @keydown=${filter ? down : undefined}
    @keyup=${filter ? up : undefined}
    @focusout=${filter ? blur : undefined}
  >${hero ? html`<div class="sdr-metric__info">${info}${hint}</div>` : info}${chart}${hero ? nothing : hint}</div>`;
}

let drawn = 0;
const still: SetActive = () => {};
/** The tiles (by label) metricTile() has told to use the element: a page drawn again does not hear it twice. */
const told = new Set<string>();

/**
 * A figure with its change as a template; with onClick, a toggle button that filters. For a tile without a trend it
 * is the whole port. It holds no state, so a trend (two or more points) cannot be measured or read out here: it is
 * drawn as the system writes it before it has measured, a server render's first paint, with no marks and no
 * readout; without onClick its plot is a tab stop (role group, "<label> trend") whose arrows, Home and End are taken
 * from the page and move nothing; and every call writes new ids (the summary's, the gradient's), where the system's
 * useId keeps them. Use <sdr-metric-tile> for a trend: given one, the template says so in the console, once per tile
 * (by its label).
 */
export function metricTile(p: MetricTileProps): TemplateResult {
  if (p.trend && p.trend.length > 1 && !told.has(String(p.label))) {
    told.add(String(p.label));
    console.warn(
      `metricTile(${JSON.stringify(String(p.label))}): a trend needs <sdr-metric-tile>. The template cannot measure it or read it out (no marks, no readout)${
        p.onClick ? ', its arrow keys move nothing' : ', its plot is a tab stop whose keys move nothing'
      }, and its ids are new on every call.`,
    );
  }
  const id = ++drawn;
  return drawTile(p, {
    at: null,
    byKey: false,
    set: still,
    point: still,
    summaryId: `sdr-metric-t${id}`,
    gid: `sdr-trend-t${id}`,
    size: null,
  });
}

let tiles = 0;
let trends = 0;

/** <sdr-metric-tile>: the tile with its trend, measured and read out (see the header). */
export class SdrMetricTile extends LitElement {
  static override properties = {
    label: { attribute: false },
    value: { attribute: false },
    unit: { attribute: false },
    delta: { attribute: false },
    trend: { attribute: false },
    trendPrevious: { attribute: false },
    trendLabels: { attribute: false },
    trendNames: { attribute: false },
    trendFormat: { attribute: false },
    variant: { attribute: false },
    hint: { attribute: false },
    badge: { attribute: false },
    pressed: { attribute: false },
    onClick: { attribute: false },
    tileClass: { attribute: false },
    active: { state: true },
    size: { state: true },
  };

  declare label: string;
  declare value: number | string;
  declare unit: string | undefined;
  declare delta: MetricDelta | undefined;
  declare trend: number[] | undefined;
  declare trendPrevious: number[] | undefined;
  declare trendLabels: Array<Date | string> | undefined;
  declare trendNames: [string, string] | undefined;
  declare trendFormat: ((value: number) => string) | undefined;
  declare variant: 'default' | 'hero' | undefined;
  declare hint: unknown;
  declare badge: unknown;
  declare pressed: boolean | undefined;
  declare onClick: ((event: MouseEvent | KeyboardEvent) => void) | undefined;
  /** The system's className, on the tile's root. */
  declare tileClass: string | undefined;
  /** The readout: the point shown and whether a key put it there (the system's useState). */
  protected declare active: { at: number | null; byKey: boolean };
  /** What the plot measured, [width, height]; null before it has (the system's useBoxSize). */
  protected declare size: [number, number] | null;

  /** The summary's id (the system's useId, once per tile). */
  private readonly summaryId = `sdr-metric-${++tiles}`;
  /** The gradient's id: the Trend's useId, new with every trend that mounts. */
  private gid = '';
  /** The plot the ref holds now, the one the observer watches, and the one the gradient id was made for. */
  private plotNode: Element | null = null;
  private watched: Element | null = null;
  private gidFor: Element | null = null;
  private stop: (() => void) | null = null;
  /** True while Lit commits: React's events are off then, so its handlers never hear what the commit causes. */
  private committing = false;
  /**
   * The updates waiting for the next task, in order, while a pointer's waits (React's continuous lane): until then
   * the tile draws, and its handlers read, what it drew before. Null when none waits.
   */
  private queued: [number | null, boolean][] | null = null;

  constructor() {
    super();
    this.active = { at: null, byKey: false };
    this.size = null;
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this, { minWidth: '0px' });
    // Back in the page: Lit hands the plot to the ref again; measure it again, as a mount would.
    if (this.hasUpdated) this.watch();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.unwatch();
    // No pointerleave or blur comes after a removal: the readout goes with the page, as the system's state would.
    this.setActive(null, false);
  }

  /** Until what the tile measured after a render has been drawn too. */
  protected override async getUpdateComplete(): Promise<boolean> {
    let done = await super.getUpdateComplete();
    while (!done) done = await super.getUpdateComplete();
    return done;
  }

  protected override update(changed: PropertyValues<this>) {
    this.committing = true;
    try {
      super.update(changed);
    } finally {
      this.committing = false;
    }
  }

  protected override updated(changed: PropertyValues<this>) {
    super.updated(changed);
    this.watch();
  }

  /** The ref's callback: the plot's element, or undefined when it goes. */
  private readonly plotRef = (el?: Element): void => {
    this.plotNode = el ?? null;
  };

  /**
   * The system's setActive: a new state only when it differs, so the same point twice draws nothing. Nothing the
   * commit causes (the focused plot blurred as the trend goes) is heard.
   */
  private readonly setActive: SetActive = (at, byKey) => {
    if (this.committing) return;
    const o = this.active;
    if (o.at === at && o.byKey === byKey) return;
    // Behind a pointer's update a key's or a focus's waits too: React applies its queue in order, the last one wins.
    if (this.queued) this.queued.push([at, byKey]);
    else this.active = { at, byKey };
  };

  /**
   * The pointer's setActive (a move over the plot, the leave): drawn in the next task, as React renders a pointer's
   * update. Chrome sends a click's focus right after the move it held back, in the same task, so the system's focus
   * handler still sees no point and shows the latest (R10f, found in Chrome).
   */
  private readonly setPointer: SetActive = (at, byKey) => {
    if (this.committing) return;
    const o = this.active;
    if (o.at === at && o.byKey === byKey) return;
    if (!this.queued) {
      this.queued = [];
      afterTask(() => {
        const last = this.queued?.at(-1);
        this.queued = null;
        if (last) this.setActive(last[0], last[1]);
      });
    }
    this.queued.push([at, byKey]);
  };

  /** The system's useBoxSize, a layout effect on the Trend's mount: measure now, then on every resize. */
  private watch() {
    const plot = this.plotNode;
    if (plot === this.watched) return;
    this.unwatch();
    this.watched = plot;
    if (!plot) {
      // The trend went (React unmounts its Trend): what it measured goes with it.
      if (this.size) this.size = null;
      return;
    }
    if (plot !== this.gidFor) {
      this.gidFor = plot;
      this.gid = `sdr-trend-${++trends}`;
    }
    const measure = () => {
      const b = plot.getBoundingClientRect();
      const w = Math.round(b.width);
      const ht = Math.round(b.height);
      const o = this.size;
      if (o && o[0] === w && o[1] === ht) return;
      this.size = [w, ht];
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(plot);
    this.stop = () => ro.disconnect();
  }

  private unwatch() {
    const stop = this.stop;
    this.stop = null;
    this.watched = null;
    stop?.();
  }

  override render() {
    return drawTile(
      {
        label: this.label,
        value: this.value,
        unit: this.unit,
        delta: this.delta,
        trend: this.trend,
        trendPrevious: this.trendPrevious,
        trendLabels: this.trendLabels,
        trendNames: this.trendNames,
        trendFormat: this.trendFormat,
        variant: this.variant,
        hint: this.hint,
        badge: this.badge,
        pressed: this.pressed,
        onClick: this.onClick,
        className: this.tileClass,
      },
      {
        at: this.active.at,
        byKey: this.active.byKey,
        set: this.setActive,
        point: this.setPointer,
        summaryId: this.summaryId,
        gid: this.gid,
        size: this.size,
        plot: this.plotRef,
      },
    );
  }
}

if (!customElements.get('sdr-metric-tile')) customElements.define('sdr-metric-tile', SdrMetricTile);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-metric-tile': SdrMetricTile;
  }
}

export interface ProgressMeterProps {
  label: string;
  value: number;
  /** 100 when missing or 0. */
  max: number;
  unit?: string;
  /**
   * 'limit' (the default): the share of a limit, a meter that warns at its thresholds. 'progress': how far a task
   * has come, a progressbar without thresholds unless given; give it a hint that says what a tone means.
   */
  kind?: 'limit' | 'progress';
  /** The share of max where it turns caution (0.8 for a limit, none for progress); the tick sits there unless it is 1 or more. */
  cautionAt?: number;
  /** The share of max where it turns critical (1 for a limit, none for progress). */
  criticalAt?: number;
  /** Overrides what the thresholds say. */
  tone?: 'normal' | 'caution' | 'critical';
  /** What happens next ("760 left until 00:00 CET"); null hides the footnote. */
  hint?: unknown;
  className?: string;
}

/** How much of a limit is used (a send cap, an invite cap), or how far a task has come (kind 'progress'). */
export function progressMeter(p: ProgressMeterProps): TemplateResult {
  const max = p.max || 100;
  const v = Math.max(0, p.value || 0);
  const ratio = Math.min(1, v / max);
  const progress = p.kind === 'progress';
  const cautionAt = p.cautionAt != null ? p.cautionAt : progress ? null : 0.8;
  const criticalAt = p.criticalAt != null ? p.criticalAt : progress ? null : 1;
  const tone =
    p.tone ||
    (criticalAt != null && v / max >= criticalAt
      ? 'critical'
      : cautionAt != null && v / max >= cautionAt
        ? 'caution'
        : 'normal');
  const defaultFoot = progress
    ? null
    : tone === 'critical'
      ? 'Cap reached'
      : tone === 'caution'
        ? 'Approaching cap'
        : null;
  const foot = p.hint !== undefined ? p.hint : defaultFoot;
  return html`<div class=${cx('sdr-meter', `sdr-meter--${tone}`, p.className)}>
    <div class="sdr-meter__head">
      <span>${p.label}</span>
      <span class="sdr-meter__value"
        >${num(v)}<span class="sdr-meter__max">${` / ${num(max)}${p.unit ? ` ${p.unit}` : ''}`}</span></span
      >
    </div>
    <div
      class="sdr-meter__track"
      role=${progress ? 'progressbar' : 'meter'}
      aria-label=${ifDefined(p.label)}
      aria-valuemin="0"
      aria-valuemax=${max}
      aria-valuenow=${Math.min(v, max)}
      aria-valuetext=${ifDefined(v > max ? `${num(v)} of ${num(max)}${p.unit ? ` ${p.unit}` : ''}` : undefined)}
    >
      <div class="sdr-meter__fill" style=${`width:${(ratio * 100).toFixed(1)}%`}></div>
      ${
        cautionAt != null && cautionAt < 1
          ? html`<span class="sdr-meter__tick" style=${`left:${cautionAt * 100}%`}></span>`
          : null
      }
    </div>
    ${
      present(foot)
        ? html`<div class="sdr-meter__foot">${tone !== 'normal' ? icon({ name: 'alert', size: 12 }) : null}${node(foot)}</div>`
        : null
    }
  </div>`;
}
